import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.blog.category.manage',
  category: 'marketing',
  title: "Créer une catégorie d'articles (blog)",
  intent:
    'Créer et organiser les catégories du blog pour classer vos articles ; chaque article est ensuite rangé dans une catégorie.',
  context: {
    why: "Un blog qui grandit sans catégories devient vite illisible : le visiteur ne trouve plus ce qu'il cherche, et les moteurs de recherche peinent à comprendre de quoi parle votre site. Les catégories règlent les deux problèmes d'un coup. Elles guident le lecteur — « Conseils », « Actualités », « Coulisses » — vers les articles qui l'intéressent, et elles renforcent le référencement en regroupant vos contenus par thème, ce que Google valorise. On crée les catégories d'abord, une bonne fois, puis on y range les articles au fil de leur publication. Un article porte une seule catégorie : celle qui décrit le mieux son angle principal.",
    use_cases: [
      'Structurer le blog avec des catégories comme « Conseils », « Actualités », « Coulisses ».',
      'Choisir, pour un article qui touche à plusieurs sujets, la catégorie qui correspond à son angle principal.',
      'Renommer ou réorganiser vos catégories à mesure que votre blog évolue.',
    ],
  },
  required_scopes: ['website.news.create', 'website.news.update', 'website.news.delete'],
  ui_entry_points: [
    { route: '/marketing/publications', element: 'Bouton « Gérer les catégories » dans le volet des publications' },
  ],
  api: {
    primary_operation_id: 'ProNewsTagAppController_create',
    related_operation_ids: [
      'ProNewsTagAppController_list',
      'ProNewsTagAppController_update',
      'ProNewsTagAppController_delete',
    ],
  },
  steps: [
    '**Ouvrez les publications** depuis Communication, écran « Publications » (le blog).',
    "**Ouvrez « Gérer les catégories »** dans le volet de gauche : c'est là que tout se passe (créer, renommer, supprimer).",
    "**Créez vos catégories d'abord**, avant de rédiger vos articles : bouton « Ajouter une catégorie ».",
    '**Nommez la catégorie** (par exemple « Conseils ») et donnez-lui une couleur pour la repérer.',
    '**Enregistrez** : la catégorie est prête à recevoir des articles.',
    '**Renommez ou supprimez** une catégorie à tout moment depuis cette même fenêtre : chaque ligne porte ses actions.',
    '**Filtrez vos publications** en cliquant sur une catégorie dans le volet de gauche.',
  ],
  gotchas: [
    '**Créez les catégories AVANT les articles.** Pour ranger un article dans une catégorie au moment de le rédiger, il faut que la catégorie existe déjà. Prenez donc cinq minutes au départ pour poser vos grandes rubriques.',
    "**Un article ne porte qu'une seule catégorie.** Le rangement est exclusif : un article « conseils de saison » va soit dans « Conseils », soit dans « Actualités ». Choisissez l'angle principal — c'est aussi celui sur lequel vos lecteurs filtreront.",
    '**Des catégories claires aident le référencement.** Regrouper vos contenus par thème aide Google à comprendre votre site et à mieux le positionner. Préférez quelques catégories nettes à une multitude de rubriques qui se recoupent.',
    '**Gardez peu de catégories, bien nommées.** Trois à six rubriques suffisent le plus souvent. Trop de catégories diluent le classement et perdent le lecteur autant que pas de catégorie du tout.',
  ],
  related_features: ['marketing.blog.article.create', 'website.builder.pages'],
  demo_script: {
    duration_target_s: 30,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Des catégories rendent votre blog lisible et aident le référencement — créons-en une.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/publications'), caption: 'Puis « Blog ».' },
      {
        action: 'click',
        target: TEST_IDS.publication_category_manage,
        caption: 'Ouvrez « Gérer les catégories » : créer, renommer et supprimer se font au même endroit.',
      },
      {
        action: 'click',
        target: TEST_IDS.publication_category_add,
        caption: 'Ajoutez une catégorie, à créer avant de rédiger vos articles.',
      },
      { action: 'wait_for', target: fieldTestId('title'), caption: 'Le formulaire de catégorie s’ouvre.' },
      {
        action: 'fill',
        target: fieldTestId('title'),
        value: 'Conseils',
        caption: 'Nommez la catégorie, par exemple « Conseils » : elle guidera le lecteur vers les bons articles.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Dans Blog, deux choses : on crée d'abord la catégorie (on peut en avoir
// > plusieurs), puis les articles ; un article peut être affecté à une ou plusieurs catégories.
//
// ⚠️ Le produit livré n'a PAS suivi le verbatim sur ce point : `maxNewsTagSelectable = 1`
// (packages/shared/src/lib/pro_news.ts) cape `pro_news_tags` à `maxItems: 1` en création comme
// en mise à jour, et le filtre du volet est mono-sélection. Le manifeste décrit donc une seule
// catégorie par article — il alimente Pulse/MCP, et promettre le multi-catégories ferait
// conseiller au pro une manipulation que l'API rejette en validation.
